/**
 * Lightweight ViewCube + Zoom-to-Fit overlay for the BIM viewer.
 *
 * Pure Three.js (no @thatopen/ui-obc dependency, no upstream upgrade required).
 *
 * Features:
 * - 100x100 px overlay anchored top-right of the viewer container.
 * - 6 cube faces (FRONT / BACK / LEFT / RIGHT / TOP / BOTTOM) with cyan theme.
 *   Click → main camera animates to that orthogonal view.
 * - Cube auto-syncs orientation with the main camera every render.
 * - "Zoom to Fit" icon button to the LEFT of the cube → fits all loaded
 *   FragmentsModels' combined bounding sphere.
 *
 * Public API:
 *   const cube = createViewCubeOverlay(container, world, getAllModelObjects);
 *   cube.dispose();
 */
import * as THREE from "three";

const FACES = ["right", "left", "top", "bottom", "front", "back"] as const;
type FaceName = typeof FACES[number];

const FACE_LABELS: Record<FaceName, string> = {
  front: "FRONT",
  back: "BACK",
  left: "LEFT",
  right: "RIGHT",
  top: "TOP",
  bottom: "BOTTOM",
};

// 主相機從目標(target)看向哪個方向 → 對應的相機相對位移 (距離為 1，外部會乘上 distance)
// 採 Y-up，主相機目前用 (X, Y, Z) 世界座標。
const FACE_DIRS: Record<FaceName, THREE.Vector3> = {
  front: new THREE.Vector3(0, 0, 1),
  back: new THREE.Vector3(0, 0, -1),
  right: new THREE.Vector3(1, 0, 0),
  left: new THREE.Vector3(-1, 0, 0),
  top: new THREE.Vector3(0, 1, 0),
  bottom: new THREE.Vector3(0, -1, 0),
};

interface MinimalWorld {
  camera: {
    three: THREE.PerspectiveCamera | THREE.OrthographicCamera;
    controls: {
      setLookAt(
        px: number, py: number, pz: number,
        tx: number, ty: number, tz: number,
        enableTransition?: boolean
      ): Promise<void>;
      fitToSphere?(sphere: THREE.Sphere, enableTransition: boolean): Promise<void>;
      getTarget(target: THREE.Vector3): THREE.Vector3;
      getPosition(target: THREE.Vector3): THREE.Vector3;
    };
  };
  scene: { three: THREE.Scene };
  renderer: { three: THREE.WebGLRenderer };
}

function makeFaceTexture(label: string): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;

  // Background — cyan tinted dark panel
  ctx.fillStyle = "#0d1f33";
  ctx.fillRect(0, 0, size, size);

  // Inner border
  ctx.strokeStyle = "#44d3de";
  ctx.lineWidth = 6;
  ctx.strokeRect(8, 8, size - 16, size - 16);

  // Label
  ctx.fillStyle = "#44d3de";
  ctx.font = "bold 48px -apple-system, 'Segoe UI', Helvetica, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, size / 2, size / 2);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

export interface ViewCubeOverlay {
  dispose(): void;
}

export function createViewCubeOverlay(
  container: HTMLElement,
  world: MinimalWorld,
  getAllModelObjects: () => THREE.Object3D[]
): ViewCubeOverlay {
  // ---------- DOM wrapper ----------
  const wrapper = document.createElement("div");
  wrapper.id = "viewcube-overlay";
  wrapper.style.cssText = `
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    z-index: 9000;
    pointer-events: none;
  `;

  // Zoom to Fit button (left of cube)
  const zoomBtn = document.createElement("button");
  zoomBtn.title = "Zoom to Fit (F)";
  zoomBtn.setAttribute("aria-label", "Zoom to Fit");
  zoomBtn.style.cssText = `
    pointer-events: auto;
    width: 40px; height: 40px;
    border: 1px solid rgba(68, 211, 222, 0.4);
    border-radius: 6px;
    background: rgba(13, 31, 51, 0.85);
    color: #44d3de;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background 120ms ease, border-color 120ms ease;
  `;
  zoomBtn.onmouseenter = () => {
    zoomBtn.style.background = "rgba(68, 211, 222, 0.18)";
    zoomBtn.style.borderColor = "#44d3de";
  };
  zoomBtn.onmouseleave = () => {
    zoomBtn.style.background = "rgba(13, 31, 51, 0.85)";
    zoomBtn.style.borderColor = "rgba(68, 211, 222, 0.4)";
  };
  // crosshair / fit-to-screen icon
  zoomBtn.innerHTML = `
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 9V5a2 2 0 0 1 2-2h4"/>
      <path d="M21 9V5a2 2 0 0 0-2-2h-4"/>
      <path d="M3 15v4a2 2 0 0 0 2 2h4"/>
      <path d="M21 15v4a2 2 0 0 1-2 2h-4"/>
      <path d="M9 9h6v6H9z"/>
    </svg>
  `;
  wrapper.appendChild(zoomBtn);

  // Cube canvas
  const cubeCanvas = document.createElement("canvas");
  cubeCanvas.width = 100;
  cubeCanvas.height = 100;
  cubeCanvas.style.cssText = `
    pointer-events: auto;
    width: 100px; height: 100px;
    background: rgba(13, 31, 51, 0.6);
    border: 1px solid rgba(68, 211, 222, 0.4);
    border-radius: 6px;
    cursor: pointer;
  `;
  wrapper.appendChild(cubeCanvas);

  // Make sure container is positioned (we anchor absolutely inside it)
  if (getComputedStyle(container).position === "static") {
    container.style.position = "relative";
  }
  container.appendChild(wrapper);

  // ---------- Three.js cube scene ----------
  const cubeRenderer = new THREE.WebGLRenderer({
    canvas: cubeCanvas,
    alpha: true,
    antialias: true,
  });
  cubeRenderer.setPixelRatio(window.devicePixelRatio);
  cubeRenderer.setSize(100, 100, false);
  cubeRenderer.setClearColor(0x000000, 0);

  const cubeScene = new THREE.Scene();

  const cubeCamera = new THREE.PerspectiveCamera(40, 1, 0.1, 100);
  // Distance chosen so a 1-unit cube fully fits without clipping
  const CAM_DISTANCE = 3.2;

  // Cube mesh — texture per face. BoxGeometry material order: +X, -X, +Y, -Y, +Z, -Z
  // i.e. right, left, top, bottom, front, back
  const materials = FACES.map(
    (f) =>
      new THREE.MeshBasicMaterial({
        map: makeFaceTexture(FACE_LABELS[f]),
        // hover/pick highlighting handled via material.color tint
      })
  );
  const cubeGeom = new THREE.BoxGeometry(1, 1, 1);
  const cubeMesh = new THREE.Mesh(cubeGeom, materials);
  cubeScene.add(cubeMesh);

  // A subtle ambient light isn't required because MeshBasicMaterial is unlit,
  // but the textured faces still benefit from a clean background.

  // ---------- Sync orientation: cube mirrors main camera direction ----------
  function updateCubeOrientation() {
    // Get main camera's view direction in world space
    const target = new THREE.Vector3();
    const pos = new THREE.Vector3();
    world.camera.controls.getTarget(target);
    world.camera.controls.getPosition(pos);

    const viewDir = new THREE.Vector3().subVectors(target, pos).normalize();

    // We want the cube to be viewed from the OPPOSITE direction of viewDir,
    // so that the face the user is looking at stays facing the user.
    const cubeViewDir = viewDir.clone().negate();

    // Position the cubeCamera along cubeViewDir from origin
    cubeCamera.position.copy(cubeViewDir).multiplyScalar(CAM_DISTANCE);

    // Up vector — try to match main camera's up; fallback to Y up
    const mainUp = new THREE.Vector3(0, 1, 0);
    if ((world.camera.three as any).up) {
      mainUp.copy((world.camera.three as any).up);
    }
    cubeCamera.up.copy(mainUp);
    cubeCamera.lookAt(0, 0, 0);
    cubeCamera.updateMatrixWorld();
  }

  // ---------- Click → snap main camera to face view ----------
  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();

  // BoxGeometry materialIndex → FaceName mapping
  const matIndexToFace: FaceName[] = ["right", "left", "top", "bottom", "front", "back"];

  function pickFace(clientX: number, clientY: number): FaceName | null {
    const rect = cubeCanvas.getBoundingClientRect();
    ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(ndc, cubeCamera);
    const hits = raycaster.intersectObject(cubeMesh, false);
    if (hits.length === 0) return null;
    const hit = hits[0];
    const matIdx = (hit.face as any)?.materialIndex ?? -1;
    return matIndexToFace[matIdx] ?? null;
  }

  function snapToFace(face: FaceName) {
    // Compute current target & camera distance so we keep zoom level
    const target = new THREE.Vector3();
    const pos = new THREE.Vector3();
    world.camera.controls.getTarget(target);
    world.camera.controls.getPosition(pos);
    const distance = pos.distanceTo(target) || 50;

    const dir = FACE_DIRS[face].clone();
    const newPos = target.clone().add(dir.multiplyScalar(distance));

    world.camera.controls.setLookAt(
      newPos.x, newPos.y, newPos.z,
      target.x, target.y, target.z,
      true
    );
  }

  cubeCanvas.addEventListener("click", (e) => {
    const face = pickFace(e.clientX, e.clientY);
    if (face) snapToFace(face);
  });

  // Hover highlight
  cubeCanvas.addEventListener("mousemove", (e) => {
    const face = pickFace(e.clientX, e.clientY);
    materials.forEach((m, i) => {
      const isHover = matIndexToFace[i] === face;
      m.color.setHex(isHover ? 0xffffff : 0xbfe8ec);
      m.needsUpdate = true;
    });
    cubeCanvas.style.cursor = face ? "pointer" : "grab";
  });
  cubeCanvas.addEventListener("mouseleave", () => {
    materials.forEach((m) => {
      m.color.setHex(0xbfe8ec);
      m.needsUpdate = true;
    });
  });

  // ---------- Zoom to Fit ----------
  function zoomToFit() {
    const objects = getAllModelObjects();
    if (objects.length === 0) {
      console.warn("[zoomToFit] no model objects");
      return;
    }
    const box = new THREE.Box3();
    let any = false;
    for (const obj of objects) {
      if (!obj) continue;
      try {
        const objBox = new THREE.Box3().setFromObject(obj);
        if (!objBox.isEmpty()) {
          box.union(objBox);
          any = true;
        }
      } catch {
        /* ignore objects that can't be boxed */
      }
    }
    if (!any || box.isEmpty()) {
      console.warn("[zoomToFit] empty bounding box");
      return;
    }
    const sphere = box.getBoundingSphere(new THREE.Sphere());

    // Try fitToSphere if available (camera-controls library), else setLookAt fallback
    const controls: any = world.camera.controls;
    if (typeof controls.fitToSphere === "function") {
      controls.fitToSphere(sphere, true);
      return;
    }

    // Fallback: position camera at 1.5× radius along current view direction
    const target = sphere.center.clone();
    const pos = new THREE.Vector3();
    controls.getPosition(pos);
    const dir = new THREE.Vector3().subVectors(pos, target);
    if (dir.lengthSq() < 1e-6) dir.set(1, 1, 1);
    dir.normalize().multiplyScalar(sphere.radius * 2.5);
    const newPos = target.clone().add(dir);
    controls.setLookAt(
      newPos.x, newPos.y, newPos.z,
      target.x, target.y, target.z,
      true
    );
  }

  zoomBtn.addEventListener("click", zoomToFit);

  // Keyboard shortcut: F = fit
  function onKeyDown(e: KeyboardEvent) {
    if (e.key === "f" || e.key === "F") {
      // Only when no input/textarea focused
      const tag = (document.activeElement?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      zoomToFit();
    }
  }
  window.addEventListener("keydown", onKeyDown);

  // ---------- Render loop ----------
  let stopped = false;
  function tick() {
    if (stopped) return;
    updateCubeOrientation();
    cubeRenderer.render(cubeScene, cubeCamera);
    requestAnimationFrame(tick);
  }
  tick();

  return {
    dispose() {
      stopped = true;
      window.removeEventListener("keydown", onKeyDown);
      cubeRenderer.dispose();
      materials.forEach((m) => {
        if (m.map) m.map.dispose();
        m.dispose();
      });
      cubeGeom.dispose();
      wrapper.remove();
    },
  };
}
